/**
 * [CurveBeamAI] Shared HTTP utilities for DMP API services.
 *
 * Provides authenticated fetch with automatic timeout, error re-throwing,
 * and base URL resolution. Used by all workflow-specific API services
 * (e.g., segmentation-review.api.ts, future bmd-rotate.api.ts, etc.).
 */

import { environment } from './config.service';
import { getAccessTokenFromStorage } from './access-validation.service';

/** Returns the DMP backend base URL with any trailing slash stripped. */
export function getApiBaseUrl(): string {
  return environment.backendBaseUrl?.replace(/\/$/, '') ?? '';
}

/** Builds an Authorization header from the stored JWT, if available. */
export function getAuthHeaders(): Record<string, string> {
  const token = getAccessTokenFromStorage();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Default network timeout applied to every API call (30 seconds). */
export const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Wrapper around the native `fetch` that adds an automatic timeout.
 *
 * If the caller also supplies its own `AbortSignal` (e.g. from a React
 * cleanup function), both the caller's abort and the timeout abort are
 * honoured - whichever fires first wins.
 *
 * @param url      - endpoint to fetch
 * @param options  - standard `RequestInit` plus an optional `timeoutMs` override
 * @returns the fetch `Response`
 */
export function fetchWithTimeout(
  url: string,
  options: RequestInit & { timeoutMs?: number } = {}
): Promise<Response> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, ...fetchOpts } = options;
  const controller = new AbortController();

  // If the caller already has an AbortSignal (e.g. from useEffect cleanup),
  // forward its abort to our internal controller so both can cancel the request.
  const existingSignal = fetchOpts.signal;
  if (existingSignal) {
    existingSignal.addEventListener('abort', () => controller.abort());
  }

  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...fetchOpts, signal: controller.signal }).finally(() => clearTimeout(timer));
}

/**
 * Re-throws an unknown error with additional context while preserving the
 * original error as `cause` for stack-trace debugging.
 */
export function rethrow(e: unknown, message: string): never {
  if (e instanceof Error) throw new Error(`${message}: ${e.message}`, { cause: e });
  throw new Error(`${message}: ${String(e)}`);
}
